// src/contexts/ChatContext.tsx
import React, { createContext, useContext, useState, useEffect, ReactNode, CSSProperties } from 'react';
import ChatSessionWindow from '../components/rag/chat/ChatSessionWindow';

interface ChatWindow {
    id: string;
    name: string;
}

interface ChatContextType {
    activeChats: ChatWindow[];
    isAnyWindowFullscreen: boolean;
    openChat: (chat: ChatWindow) => void;
    closeChat: (chatId: string) => void;
    setFullscreenState: (isFullscreen: boolean) => void;
}

const ChatContext = createContext<ChatContextType | undefined>(undefined);

export const ChatProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const [activeChats, setActiveChats] = useState<ChatWindow[]>([]);
    const [isAnyWindowFullscreen, setIsAnyWindowFullscreen] = useState(false);
    const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

    // Generate temporary userId if not present
    useEffect(() => {
        if (!localStorage.getItem('userId')) {
            const tempUserId = 'temp-' + Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15);
            console.log('[Debug] Setting temporary userId:', tempUserId);
            localStorage.setItem('userId', tempUserId);
        }
    }, []);

    // Listen for window resize to detect mobile/desktop
    useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth < 768);
        };

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // Handle body scroll locking for mobile
    useEffect(() => {
        if (isMobile && activeChats.length > 0) {
            // Store the current scroll position
            const scrollY = window.scrollY;

            // Lock body scroll
            document.body.style.overflow = 'hidden';
            document.body.style.position = 'fixed';
            document.body.style.top = `-${scrollY}px`;
            document.body.style.width = '100%';
            document.body.style.left = '0';
            document.body.classList.add('has-mobile-chat');
        } else {
            // Restore normal scrolling
            const scrollY = document.body.style.top ?
                parseInt(document.body.style.top || '0', 10) * -1 :
                0;

            document.body.style.overflow = '';
            document.body.style.position = '';
            document.body.style.top = '';
            document.body.style.width = '';
            document.body.style.left = '';
            document.body.classList.remove('has-mobile-chat');

            // Restore scroll position
            window.scrollTo(0, scrollY);
        }

        return () => {
            // Cleanup on unmount
            const scrollY = document.body.style.top ?
                parseInt(document.body.style.top || '0', 10) * -1 :
                0;

            document.body.style.overflow = '';
            document.body.style.position = '';
            document.body.style.top = '';
            document.body.style.width = '';
            document.body.style.left = '';
            document.body.classList.remove('has-mobile-chat');

            window.scrollTo(0, scrollY);
        };
    }, [isMobile, activeChats.length]);

    const openChat = (chat: ChatWindow) => {
        console.log('[Debug] Opening chat:', {
            chatId: chat.id,
            chatName: chat.name,
            isMobile,
            currentActiveChats: activeChats.length
        });

        // Ensure we have a userId before opening chat
        if (!localStorage.getItem('userId')) {
            const tempUserId = 'temp-' + Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15);
            console.log('[Debug] Setting temporary userId before opening chat:', tempUserId);
            localStorage.setItem('userId', tempUserId);
        }

        if (isMobile) {
            console.log('[Debug] Mobile mode: replacing all chats');
            setActiveChats([chat]);
            setIsAnyWindowFullscreen(false);
        } else {
            console.log('[Debug] Desktop mode: adding chat if not exists');
            setActiveChats(prev => {
                const chatExists = prev.find(c => c.id === chat.id);
                console.log('[Debug] Chat exists:', chatExists);
                if (!chatExists) {
                    return [...prev, chat];
                }
                return prev;
            });
        }
    };

    const closeChat = (chatId: string) => {
        setActiveChats(prev => prev.filter(chat => chat.id !== chatId));
    };

    const setFullscreenState = (isFullscreen: boolean) => {
        setIsAnyWindowFullscreen(isFullscreen);
    };

    // Mobile chat styling
    const mobileChatStyle: CSSProperties = {
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        width: '100vw',
        height: '100vh',
        zIndex: 9999,
        backgroundColor: '#ffffff',
        margin: 0,
        padding: 0,
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column'
    };

    return (
        <ChatContext.Provider value={{
            activeChats,
            isAnyWindowFullscreen,
            openChat,
            closeChat,
            setFullscreenState
        }}>
            {/* Main content */}
            <div style={{
                position: 'relative',
                minHeight: '100vh'
            }}>
                {children}
            </div>

            {/* Separate chat windows container */}
            <div
                id="chat-windows-container"
                style={{
                    position: 'fixed',
                    inset: 0,
                    pointerEvents: 'none',
                    zIndex: 1500,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'flex-end',
                    justifyContent: 'flex-end',
                    padding: '20px'
                }}
            >
                {activeChats.map((chat, index) => (
                    <div
                        key={chat.id}
                        style={{
                            pointerEvents: 'auto',
                            marginBottom: index === activeChats.length - 1 ? 0 : '10px'
                        }}
                    >
                        {isMobile ? (
                            <div id="mobile-chat-overlay" style={mobileChatStyle}>
                                <ChatSessionWindow
                                    applicationId={chat.id}
                                    applicationName={chat.name}
                                    onClose={() => closeChat(chat.id)}
                                    position={index}
                                    isMobile={true}
                                />
                            </div>
                        ) : (
                            <ChatSessionWindow
                                applicationId={chat.id}
                                applicationName={chat.name}
                                onClose={() => closeChat(chat.id)}
                                position={index}
                                isMobile={false}
                            />
                        )}
                    </div>
                ))}
            </div>
        </ChatContext.Provider>
    );
};

export const useChatContext = () => {
    const context = useContext(ChatContext);
    if (!context) {
        throw new Error('useChatContext must be used within a ChatProvider');
    }
    return context;
};